import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useMe } from '@/auth/useAuth';
import { api } from '@/lib/api';

import { PortalLocaleContext, type PortalLocaleContextValue } from './PortalLocaleContext';
import {
  PORTAL_HTML_LANG,
  createPortalTranslate,
  readStoredPortalLocale,
  resolvePortalLocale,
  writeStoredPortalLocale,
  type PortalLocale,
} from './portalLocale';

interface FamilyLanguage {
  preferred_language?: string | null;
}

export function PortalLocaleProvider({ children }: { children: ReactNode }) {
  const me = useMe('user');
  const familyId = me.data?.kind === 'user' ? me.data.family_id : null;
  // Same query key as SettingsPage, so both read one cached family record.
  const family = useQuery<FamilyLanguage>({
    queryKey: ['family', familyId],
    queryFn: () => api<FamilyLanguage>(`/families/${familyId}`),
    enabled: !!familyId,
  });
  const [deviceChoice, setDeviceChoice] = useState<PortalLocale | null>(readStoredPortalLocale);
  const locale = resolvePortalLocale(deviceChoice, family.data?.preferred_language);

  useEffect(() => {
    const root = document.documentElement;
    const previousLang = root.lang;
    root.lang = PORTAL_HTML_LANG[locale];
    return () => {
      root.lang = previousLang;
    };
  }, [locale]);

  const setLocale = useCallback((next: PortalLocale) => {
    writeStoredPortalLocale(next);
    setDeviceChoice(next);
  }, []);

  const value = useMemo<PortalLocaleContextValue>(
    () => ({ locale, setLocale, t: createPortalTranslate(locale) }),
    [locale, setLocale],
  );

  return <PortalLocaleContext.Provider value={value}>{children}</PortalLocaleContext.Provider>;
}
